/**
 * Centralized platform registry — the single source of truth for all
 * supported IDE/agent platforms. Adding a new platform is a one-object change
 * here (plus an SVG icon asset and install component in the frontend).
 *
 * Everything else — ClientType enum, extension schemas, trusted-extension
 * schemas, Shadow AI correlation, UI labels, YOLO mode scanning — is derived
 * from this registry.
 */
// ---------------------------------------------------------------------------
// AI instructions detection types
// ---------------------------------------------------------------------------
/**
 * AI Instructions Inventory bucket. Path-based classifiers resolve only to
 * `rule | command | skill`; `configuration` is assigned at ingest for virtual
 * sources (e.g. `vscode://settings`) — see `storeAiInstruction`.
 *
 * Single source of truth for both the type and the runtime values — adding
 * a member here automatically extends every consumer that iterates
 * `AI_INSTRUCTION_CATEGORIES` (e.g. boilerplate-name detection, default-name
 * generation in the AI summary fallback path).
 */
export const AI_INSTRUCTION_CATEGORIES = [
    'rule',
    'command',
    'skill',
    'configuration',
];
/**
 * Display labels for the AI-instruction classification (KIR-4432).
 *
 * The single source of truth for what the "Type" badge reads. The frontend kept
 * its own copy (`CATEGORY_BADGE_LABELS`) and the public `/api/v1` contract
 * published the bare category, so a customer saw `configuration` where the app
 * says "Configuration".
 */
export const AI_INSTRUCTION_CATEGORY_LABELS = Object.freeze({
    rule: 'Rule',
    command: 'Command',
    skill: 'Skill',
    configuration: 'Configuration',
});
/**
 * The display label for an AI-instruction category.
 *
 * Falls back to capitalising the first letter only — deliberately *not* a
 * per-word title-case, which would render a hyphenated future category as
 * "Ide-setting". The classifier is free to emit a category this map does not
 * know, which is why the published `type` stays an open string.
 */
export function getAiInstructionCategoryLabel(category) {
    return (AI_INSTRUCTION_CATEGORY_LABELS[category] ??
        category.charAt(0).toUpperCase() + category.slice(1));
}
// ---------------------------------------------------------------------------
// Per-platform AI instructions configs
// Each entry's `pattern` is lowercase forward-slash with leading '/' so
// the classifier (apps/backend/src/domain/handlers/ai-instructions/
// classify-ai-instruction.ts) can normalize Windows / POSIX / mixed-case
// inputs against a single canonical form.
// ---------------------------------------------------------------------------
const CURSOR_AI_INSTRUCTIONS = {
    rules: [
        {
            pattern: '/.cursor/rules/',
            matchType: 'directory',
            category: 'rule',
            locality: 'workspace',
            // BW-compat: Cursor stores some structured rules as `.json` (the
            // Cursor extension's `cursorAiInstructionsMonitor` watches
            // `**/.cursor/rules/**/*.{md,mdc,json}`), and the legacy backend
            // path detector admitted them as rule files. Disable the default
            // markdown-only gate so JSON rules continue to classify after the
            // registry-driven refactor. Other Cursor directories below keep
            // the markdown-only default because they have no JSON counterpart.
            requireMarkdownExt: false,
        },
        {
            pattern: '/.cursor/commands/',
            matchType: 'directory',
            category: 'command',
            locality: 'workspace',
        },
        {
            pattern: '/.cursor/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'workspace',
        },
        {
            pattern: '/.cursor/rules.json',
            matchType: 'absolute',
            category: 'rule',
            locality: 'workspace',
        },
        {
            pattern: '/.cursorrules',
            matchType: 'absolute',
            category: 'rule',
            locality: 'workspace',
        },
        {
            pattern: 'agents.md',
            matchType: 'basename',
            category: 'rule',
            locality: 'workspace',
        },
    ],
};
const CLAUDE_AI_INSTRUCTIONS = {
    rules: [
        {
            pattern: '/.claude/agents/',
            matchType: 'directory',
            category: 'rule',
            locality: 'workspace',
        },
        {
            pattern: '/.claude/commands/',
            matchType: 'directory',
            category: 'command',
            locality: 'workspace',
        },
        {
            pattern: '/.claude/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'workspace',
        },
        {
            pattern: 'claude.md',
            matchType: 'basename',
            category: 'rule',
            locality: 'workspace',
            requireMarkdownExt: true,
        },
        {
            pattern: 'claude.local.md',
            matchType: 'basename',
            category: 'rule',
            locality: 'workspace',
            requireMarkdownExt: true,
        },
        {
            // Claude Code reads the cross-tool AGENTS.md sibling as an instruction
            // file, matching classify.go's FileTypeInstruction tag. `agents.md` is
            // shared with cursor/codex; a Claude-owned post resolves here only via
            // preferredPlatformId (cursor is declared first, so no-preference wins go
            // to cursor). Claimed alongside agents.override.md so both siblings
            // attribute consistently for Claude.
            pattern: 'agents.md',
            matchType: 'basename',
            category: 'rule',
            locality: 'workspace',
            requireMarkdownExt: true,
        },
        {
            // Cross-tool override sibling of AGENTS.md (KIR-3895), same tag path.
            pattern: 'agents.override.md',
            matchType: 'basename',
            category: 'rule',
            locality: 'workspace',
            requireMarkdownExt: true,
        },
        // ── plugin cache (KIR-3309) ──────────────────────────────────────────
        // Marketplace plugins are copied to `~/.claude/plugins/cache/
        // <marketplace>/<plugin>/<version>/<component>/…`. The `.claude/<comp>/`
        // rules above do NOT match those paths (extra segments in between), so
        // before KIR-3309 only `SKILL.md` was inventoried — via a broad
        // claude_desktop cache rule, now removed. The cache is Claude Code's own
        // directory, so the rules live here.
        //
        // `componentDepthFromScope: 3` pins the component to the version-root
        // child slot, mirroring the agent's `defaultCacheClassify`
        // (extensions/agent/platforms/claude_code/plugin_manifest.go) so a decoy
        // `<ver>/docs/commands/x.md` does not match. `rules` is deliberately
        // absent: a Claude Code plugin has no `rules/` component, and
        // `cacheComponentFileType` omits it on the agent side too.
        //
        // KNOWN LIMITATION (out of scope for KIR-3309): a plugin whose
        // `.claude-plugin/plugin.json` REMAPS a component to a custom path
        // (`commands` / `agents` / `outputStyles` / `skills` accept explicit
        // paths) is still not inventoried. These rules are path-shape only and
        // cannot resolve a manifest-declared path, and neither can the agent's
        // hint (pkg/scanner uses the default-layout helper) nor
        // `detectFileCategory`. Such files are still discovered and threat-scanned
        // — only the inventory row is missing. Fixing it means routing the scan
        // path through the pluginhost's manifest resolver; track separately.
        {
            pattern: 'commands',
            matchType: 'scopedComponent',
            scope: '/.claude/plugins/cache/',
            componentDepthFromScope: 3,
            category: 'command',
            locality: 'global',
        },
        {
            pattern: 'agents',
            matchType: 'scopedComponent',
            scope: '/.claude/plugins/cache/',
            componentDepthFromScope: 3,
            category: 'rule',
            locality: 'global',
        },
        {
            pattern: 'output-styles',
            matchType: 'scopedComponent',
            scope: '/.claude/plugins/cache/',
            componentDepthFromScope: 3,
            category: 'rule',
            locality: 'global',
        },
        // basenameFilter limits inventory rows to the SKILL.md manifest;
        // supporting `.md` siblings inside a skill folder still classify as
        // `skill` in policy mode so checkSkills (not checkRules) gates them.
        // scopeRootBasename keeps the version-root `<ver>/SKILL.md` manifest —
        // which Claude Code loads without any `skills/` dir in the path —
        // inventoried as it was before KIR-3309.
        {
            pattern: 'skills',
            matchType: 'scopedComponent',
            scope: '/.claude/plugins/cache/',
            componentDepthFromScope: 3,
            scopeRootBasename: 'SKILL.md',
            category: 'skill',
            locality: 'global',
            basenameFilter: 'SKILL.md',
        },
    ],
};
const COPILOT_AI_INSTRUCTIONS = {
    rules: [
        {
            pattern: '/.github/copilot-instructions.md',
            matchType: 'absolute',
            category: 'rule',
            locality: 'workspace',
        },
        {
            pattern: '/.github/instructions/',
            matchType: 'directory',
            category: 'rule',
            locality: 'workspace',
        },
        {
            pattern: '/.github/prompts/',
            matchType: 'directory',
            category: 'rule',
            locality: 'workspace',
        },
        {
            pattern: '/.github/agents/',
            matchType: 'directory',
            category: 'rule',
            locality: 'workspace',
        },
        // Project skills. Copilot CLI claims the same path with the same category
        // (KIR-4747). basenameFilter keeps one inventory row per skill folder.
        {
            pattern: '/.github/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'workspace',
            basenameFilter: 'SKILL.md',
        },
        {
            pattern: '/.copilot/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'global',
        },
        {
            pattern: '/.copilot/prompts/',
            matchType: 'directory',
            category: 'rule',
            locality: 'global',
        },
    ],
};
/**
 * Claude Desktop's agent-session roots, as `scopedComponent` scopes (lowercase,
 * leading AND trailing `/`).
 *
 * Claude Desktop keeps two session roots side by side: `claude-code-sessions`
 * (Code tab) and `local-agent-mode-sessions` (Cowork/Chat), in the same
 * per-session layout. Both are live, so both are scoped (KIR-3978).
 *
 * ⚠️ `claude-code-sessions` is Claude **Desktop**'s directory despite the name —
 * it is unrelated to Claude **Code** session transcripts
 * (`~/.claude/projects/**.jsonl`), which are matched elsewhere.
 *
 * Parity contract: mirrored by `claudeDesktopSessionsDirSegments` in
 * extensions/agent/pkg/mcp/claude_desktop_session.go and
 * `claudeDesktopSessionsSegments` in
 * extensions/agent/helpers/filewatcher/claude_components.go. Change one,
 * change all three.
 */
const CLAUDE_DESKTOP_SESSION_SCOPES = [
    '/claude-code-sessions/',
    '/local-agent-mode-sessions/',
];
/**
 * The component rules a Claude Desktop session bundle contributes, expanded
 * across every scope in {@link CLAUDE_DESKTOP_SESSION_SCOPES}.
 *
 * Session bundles (Claude Desktop's session-scoped plugins) use the same
 * `.claude-plugin/plugin.json` component layout as Claude Code plugins —
 * `commands` / `agents` / `output-styles` / `skills`. Before KIR-3309 only
 * `SKILL.md` was admitted, so a bundle's commands/agents/output-styles were
 * threat-scanned but never inventoried.
 *
 * Each `scope` is a bare directory segment, distinctive enough on its own that
 * one rule per component per scope covers every OS — macOS `~/Library/
 * Application Support/Claude/…`, Linux `~/.config/Claude/…`, Windows
 * `%APPDATA%\Claude\…`. `componentDepthFromScope` is deliberately omitted: the
 * session tree nests as `<session>/<sub>/[rpm/plugin_x/]<component>/…`, so the
 * component's depth varies.
 *
 * Generated rather than written out twice so a scope can never be added to one
 * component and forgotten on the others.
 */
const CLAUDE_DESKTOP_SESSION_COMPONENT_RULES = CLAUDE_DESKTOP_SESSION_SCOPES.flatMap(scope => [
    {
        pattern: 'commands',
        matchType: 'scopedComponent',
        scope,
        category: 'command',
        locality: 'system',
    },
    {
        pattern: 'agents',
        matchType: 'scopedComponent',
        scope,
        category: 'rule',
        locality: 'system',
    },
    {
        pattern: 'output-styles',
        matchType: 'scopedComponent',
        scope,
        category: 'rule',
        locality: 'system',
    },
    // basenameFilter limits inventory rows to the SKILL.md manifest;
    // supporting `.md` siblings inside a skill folder still classify as
    // `skill` in policy mode so checkSkills (not checkRules) gates them.
    {
        pattern: 'skills',
        matchType: 'scopedComponent',
        scope,
        category: 'skill',
        locality: 'system',
        basenameFilter: 'SKILL.md',
    },
]);
const CLAUDE_DESKTOP_AI_INSTRUCTIONS = {
    rules: [
        // Plugin-cache paths (`~/.claude/plugins/cache/…`) are NOT here: that is
        // Claude Code's own directory and its rules moved to
        // CLAUDE_AI_INSTRUCTIONS in KIR-3309.
        ...CLAUDE_DESKTOP_SESSION_COMPONENT_RULES,
    ],
};
// GitHub Copilot CLI (the terminal `copilot` agent — distinct from the
// `copilot` IDE-extension entry). It reads the same project-level `.github/*`
// instruction files as the IDE plus its own user-level `~/.copilot/` tree.
// Shared `.github/*` paths intentionally reuse the SAME categories as
// COPILOT_AI_INSTRUCTIONS so the multi-claim category-consistency invariant
// holds (a path resolves to one category regardless of claiming platform).
const GITHUB_COPILOT_CLI_AI_INSTRUCTIONS = {
    rules: [
        // ── project-level (shared with the Copilot IDE) ──────────────────────
        {
            pattern: '/.github/copilot-instructions.md',
            matchType: 'absolute',
            category: 'rule',
            locality: 'workspace',
        },
        {
            pattern: '/.github/instructions/',
            matchType: 'directory',
            category: 'rule',
            locality: 'workspace',
        },
        {
            pattern: '/.github/prompts/',
            matchType: 'directory',
            category: 'rule',
            locality: 'workspace',
        },
        {
            pattern: '/.github/agents/',
            matchType: 'directory',
            category: 'rule',
            locality: 'workspace',
        },
        // Same category, basenameFilter, and locality as the Copilot IDE rule.
        // The IDE already posts workspace markdown; a CLI-only claim would label
        // those posts github_copilot_cli (KIR-4747).
        {
            pattern: '/.github/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'workspace',
            basenameFilter: 'SKILL.md',
        },
        // AGENTS.md is also recognized by cursor/windsurf as a `rule`; same
        // category keeps the multi-claim invariant satisfied.
        {
            pattern: 'agents.md',
            matchType: 'basename',
            category: 'rule',
            locality: 'workspace',
        },
        // ── user-level (~/.copilot, CLI-specific) ────────────────────────────
        // Personal prompt files; same category as the IDE's `/.copilot/prompts/`.
        {
            pattern: '/.copilot/prompts/',
            matchType: 'directory',
            category: 'rule',
            locality: 'global',
        },
        // Copied exactly from COPILOT_AI_INSTRUCTIONS. A different basenameFilter
        // would make inventory depend on which platform is preferred (KIR-4747).
        {
            pattern: '/.copilot/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'global',
        },
        // Personal instruction (`*.instructions.md`) + custom-agent (`*.agent.md`)
        // files, per GitHub's Copilot CLI config-dir reference. Kept in lockstep
        // with the agent's isCopilotInstructionPath discovery matcher (KIR-3179).
        {
            pattern: '/.copilot/instructions/',
            matchType: 'directory',
            category: 'rule',
            locality: 'global',
        },
        {
            pattern: '/.copilot/agents/',
            matchType: 'directory',
            category: 'rule',
            locality: 'global',
        },
    ],
};
/**
 * Generic agent-config locations read by more than one tool (KIR-4646).
 *
 * `.agents/skills/` is the cross-tool skills-installer tree (read by Codex,
 * Gemini CLI, Devin Desktop and others), so no single IDE owns it. Kept at
 * `locality: 'global'` for project copies too: a `workspace` locality would
 * strip the `.agents` segment from the stored ownership path.
 */
const MULTI_TOOL_AI_INSTRUCTIONS = {
    rules: [
        {
            pattern: '/.agents/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'global',
            basenameFilter: 'SKILL.md',
        },
    ],
};
/**
 * Windsurf / Devin Desktop instruction locations.
 *
 * Carries BOTH brands after the rebrand (KIR-3873): `.devin/**` twins for the
 * three workspace shapes plus their nine system twins, 12 rules in all. The
 * bundle's own matchers back each one — `isProjectRulesFile`,
 * `isLocalWorkflowFile` and `isLocalSkillFile` each test `.devin/<dir>/` OR
 * `.windsurf/<dir>/`, and the skill matcher requires a `/SKILL.md` suffix,
 * matching `basenameFilter` here.
 *
 * WHY 12 AND NOT 18 — three distinct reasons, deliberately not conflated:
 *
 * 1. Brand-neutral, already covers Devin (2): `agents.md` and
 *    `/.agents/skills/`. Neither is Windsurf-branded, so both already match a
 *    Devin user's files; a `.devin` twin would be meaningless. The
 *    `/.agents/skills/` rule now lives on `multi_tool` (KIR-4646), because
 *    that tree is read by several tools, not by Windsurf alone.
 * 2. Does not exist in Devin (1): `.windsurfrules`. The bundle's
 *    `localRulesFilePathSegments` is `['.windsurfrules']` with zero
 *    `.devinrules` anywhere.
 * 3. Windsurf-branded global tree (3): the `/.codeium/windsurf/*` globals.
 *    `codeiumDirPathSegments` returns `['.codeium', <channel>]` where channel
 *    is `windsurf`, `windsurf-insiders`, `windsurf-next` or `windsurf-airgap`
 *    — never Devin — and there are zero occurrences of `.codeium/devin`. Devin
 *    Desktop's global tree is Windsurf-branded and already covered; a `.devin`
 *    global pattern would claim a path nothing writes.
 *
 * Windows needs no special handling: `normalizePath` lowercases and converts
 * backslashes, so `C:\ProgramData\Devin\rules\foo.md` matches through the
 * leading-slash-stripped relative branch.
 *
 * Workflows are a DEPRECATED surface, not a growing one — the editor's own
 * header UI reads "Workspace Workflow (deprecated)" and "Global Workflow
 * (deprecated)", and there is no `/commands/` successor anywhere in the
 * bundle. Covering them is still correct because the files exist and are read.
 */
const WINDSURF_AI_INSTRUCTIONS = {
    rules: [
        // ── rules ────────────────────────────────────────────────────────────
        {
            pattern: '.windsurfrules',
            matchType: 'basename',
            category: 'rule',
            locality: 'workspace',
        },
        // AGENTS.md is also recognized by Cursor; precedence is resolved by
        // the classifier's `preferredPlatformId` parameter (see the contract
        // tests). Without a preference, cursor wins (first-declared).
        {
            pattern: 'agents.md',
            matchType: 'basename',
            category: 'rule',
            locality: 'workspace',
        },
        {
            pattern: '/.windsurf/rules/',
            matchType: 'directory',
            category: 'rule',
            locality: 'workspace',
        },
        {
            pattern: '/.codeium/windsurf/memories/global_rules.md',
            matchType: 'absolute',
            category: 'rule',
            locality: 'global',
        },
        {
            pattern: '/library/application support/windsurf/rules/',
            matchType: 'directory',
            category: 'rule',
            locality: 'system',
        },
        {
            pattern: '/etc/windsurf/rules/',
            matchType: 'directory',
            category: 'rule',
            locality: 'system',
        },
        {
            pattern: '/programdata/windsurf/rules/',
            matchType: 'directory',
            category: 'rule',
            locality: 'system',
        },
        // Devin-branded twins. The enterprise roots carry both brands because the
        // bundled language server hardcodes both — see enterpriseRootDirsFor in
        // extensions/agent/platforms/windsurf/windsurf.go for the constants.
        //
        // KNOWN LIMITATION — a REDIRECTED %ProgramData% is not classified here.
        // The agent derives that root from the environment and watches it, so on a
        // machine where %ProgramData% points at, say, D:\CorpData it watches
        // D:\CorpData\Devin. No pattern here can name that: the directory is
        // arbitrary and unknown until runtime. Consequences, in full: such files
        // get no inventory row, and in policy mode they miss the category twins
        // below and fall back to 'rule' — so they are still SCANNED, just gated by
        // checkRules and categorized wrongly. Deep-linking is not affected; the
        // agent-side resolver asks the platform for its real roots (see
        // isUnderEnterpriseRoot in internal/agent/decisions_notifier_adapter.go).
        //
        // Deliberately NOT fixed with a looser pattern. The only shape that would
        // match is a bare '/devin/rules/', which would also claim a developer's
        // /Users/devin/rules/ or any project folder named devin, and hand it
        // locality:'system' — exactly the spoof the agent's resolver is anchored
        // against. Closing this properly needs the agent to send the root it
        // matched, which is a contract change, not a pattern.
        //
        // The patterns BELOW sit on that same spectrum, one segment further along,
        // which is worth stating here because this is where a future reader will
        // look for why root anchoring was not added. matchType 'directory' is
        // substring-based — `normalized.includes(pattern)` in
        // classify-ai-instruction.ts — so '/etc/devin/rules/' also matches
        // /Users/alice/myrepo/etc/devin/rules/x.md and hands a user-writable repo
        // path locality:'system'. It takes a contrived directory layout, and the
        // pre-existing '/etc/windsurf/rules/' and '/programdata/windsurf/rules/'
        // entries have carried the same shape all along: the Devin twins double
        // the count, not the exposure. Anchoring these to the path root needs a
        // matchType the registry does not have (an 'absolute' variant), so that is
        // the shape of the fix if it ever becomes worth doing.
        {
            pattern: '/.devin/rules/',
            matchType: 'directory',
            category: 'rule',
            locality: 'workspace',
        },
        {
            pattern: '/library/application support/devin/rules/',
            matchType: 'directory',
            category: 'rule',
            locality: 'system',
        },
        {
            pattern: '/etc/devin/rules/',
            matchType: 'directory',
            category: 'rule',
            locality: 'system',
        },
        {
            pattern: '/programdata/devin/rules/',
            matchType: 'directory',
            category: 'rule',
            locality: 'system',
        },
        // ── commands (Windsurf "workflows") ──────────────────────────────────
        {
            pattern: '/.windsurf/workflows/',
            matchType: 'directory',
            category: 'command',
            locality: 'workspace',
        },
        {
            pattern: '/.codeium/windsurf/global_workflows/',
            matchType: 'directory',
            category: 'command',
            locality: 'global',
        },
        {
            pattern: '/library/application support/windsurf/workflows/',
            matchType: 'directory',
            category: 'command',
            locality: 'system',
        },
        {
            pattern: '/etc/windsurf/workflows/',
            matchType: 'directory',
            category: 'command',
            locality: 'system',
        },
        {
            pattern: '/programdata/windsurf/workflows/',
            matchType: 'directory',
            category: 'command',
            locality: 'system',
        },
        // Devin-branded twins.
        {
            pattern: '/.devin/workflows/',
            matchType: 'directory',
            category: 'command',
            locality: 'workspace',
        },
        {
            pattern: '/library/application support/devin/workflows/',
            matchType: 'directory',
            category: 'command',
            locality: 'system',
        },
        {
            pattern: '/etc/devin/workflows/',
            matchType: 'directory',
            category: 'command',
            locality: 'system',
        },
        {
            pattern: '/programdata/devin/workflows/',
            matchType: 'directory',
            category: 'command',
            locality: 'system',
        },
        // ── skills (each skill is a folder containing SKILL.md) ──────────────
        // The basenameFilter dedupes inventory rows to the canonical manifest;
        // policy-mode classification still admits supporting `.md` files so
        // they get gated by `checkSkills` (not `checkRules`).
        {
            pattern: '/.windsurf/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'workspace',
            basenameFilter: 'SKILL.md',
        },
        {
            pattern: '/.codeium/windsurf/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'global',
            basenameFilter: 'SKILL.md',
        },
        {
            pattern: '/library/application support/windsurf/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'system',
            basenameFilter: 'SKILL.md',
        },
        {
            pattern: '/etc/windsurf/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'system',
            basenameFilter: 'SKILL.md',
        },
        {
            pattern: '/programdata/windsurf/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'system',
            basenameFilter: 'SKILL.md',
        },
        // Devin-branded twins.
        {
            pattern: '/.devin/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'workspace',
            basenameFilter: 'SKILL.md',
        },
        {
            pattern: '/library/application support/devin/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'system',
            basenameFilter: 'SKILL.md',
        },
        {
            pattern: '/etc/devin/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'system',
            basenameFilter: 'SKILL.md',
        },
        {
            pattern: '/programdata/devin/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'system',
            basenameFilter: 'SKILL.md',
        },
    ],
};
// ---------------------------------------------------------------------------
// Per-platform YOLO configs
// Cursor and Copilot are handled by the hardcoded scanner in the backend.
// Only declarative-only platforms (like Devin Desktop) are defined here.
//
// KIR-3873: the human-readable strings below carry the Devin Desktop brand;
// modeId / configKey / path stay `windsurf.*` because they are identifiers.
// Note these strings are copied into a persisted YoloFinding at scan time, so
// findings written before this change keep the old label indefinitely.
// ---------------------------------------------------------------------------
const WINDSURF_YOLO = {
    detectionKeys: ['windsurf.autoExecutionPolicy'],
    rules: [
        {
            configKey: 'windsurf.autoExecutionPolicy',
            keyAccess: 'flat',
            condition: { type: 'equals', value: 'turbo' },
            finding: {
                type: 'auto_execute_all',
                modeName: 'Devin Desktop Auto-Execute',
                modeDescription: 'Devin Desktop turbo mode — all tool executions auto-approved',
                modeId: 'windsurf-auto-execute',
                severity: 'critical',
                description: 'CRITICAL: Devin Desktop auto-execution policy is set to "turbo" — all tool executions are auto-approved without confirmation. This poses severe security risks.',
                path: 'windsurf.autoExecutionPolicy',
            },
            fix: {
                type: 'setting',
                path: 'windsurf.autoExecutionPolicy',
                safeValue: 'off',
                isDefault: false,
            },
        },
        {
            configKey: 'windsurf.autoExecutionPolicy',
            keyAccess: 'flat',
            condition: { type: 'equals', value: 'auto' },
            finding: {
                type: 'auto_execute_shell',
                modeName: 'Devin Desktop Auto-Execute',
                modeDescription: 'Devin Desktop auto mode — model decides auto-execution for shell commands',
                modeId: 'windsurf-auto-execute',
                severity: 'high',
                description: 'HIGH RISK: Devin Desktop auto-execution policy is set to "auto" — the model decides which shell commands to auto-execute without confirmation.',
                path: 'windsurf.autoExecutionPolicy',
            },
            fix: {
                type: 'setting',
                path: 'windsurf.autoExecutionPolicy',
                safeValue: 'off',
                isDefault: false,
            },
        },
        {
            configKey: 'windsurf.autoExecutionPolicy',
            keyAccess: 'flat',
            condition: { type: 'equals', value: 'allowListOnly' },
            finding: {
                type: 'auto_execute_allowlist',
                modeName: 'Devin Desktop Auto-Execute',
                modeDescription: 'Devin Desktop allowlist mode — auto-approve enabled for specific allowlisted tools',
                modeId: 'windsurf-auto-execute',
                severity: 'low',
                description: 'LOW RISK: Devin Desktop auto-execution policy is set to "allowListOnly" — auto-approve is enabled but restricted to allowlisted tools.',
                path: 'windsurf.autoExecutionPolicy',
            },
        },
    ],
};
// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------
const GEMINI_AI_INSTRUCTIONS = {
    rules: [
        {
            // Project- and user-level Gemini CLI instruction file (GEMINI.md at a
            // project root or ~/.gemini/GEMINI.md). basename match covers both.
            // NOTE: only the default GEMINI.md basename is inventoried here. The
            // agent additionally honors custom `contextFileName` overrides from
            // settings.json when discovering files, so an override (e.g.
            // CONTEXT.md) is still POSTed and scanned for secrets / hidden chars,
            // but it will NOT become an AI-instruction inventory row — custom
            // context filenames are rare and intentionally out of scope here.
            pattern: 'gemini.md',
            matchType: 'basename',
            category: 'rule',
            locality: 'workspace',
            requireMarkdownExt: true,
        },
        {
            // Custom commands: user/project <root>/.gemini/commands/*.toml. This
            // rule matches the contiguous `/.gemini/commands/` segment only — it
            // intentionally does NOT claim extension-bundled commands at
            // ~/.gemini/extensions/<n>/commands/*.toml (those are scanned by the
            // agent for secrets/hidden chars and surface as plugin *features*, but
            // are not AI-instruction inventory rows — deliberate, KIR-3178).
            // Gemini command files are TOML, not markdown, so the directory-rule
            // default of requireMarkdownExt:true (which exists to stop directory
            // rules from ingesting arbitrary non-instruction files) would reject
            // every command file. Override to false: the `.gemini/commands/`
            // segment is itself specific enough that everything under it is an
            // instruction surface. Without this, command tomls never become
            // inventory rows (KIR-3178).
            pattern: '/.gemini/commands/',
            matchType: 'directory',
            category: 'command',
            locality: 'workspace',
            requireMarkdownExt: false,
        },
    ],
};
const CODEX_AI_INSTRUCTIONS = {
    rules: [
        {
            // OpenAI Codex reads AGENTS.md (and the AGENTS.override.md sibling) from
            // project roots as its instruction files — the cross-tool AGENTS.md
            // standard. basename match covers both project-root and nested copies.
            // Cursor also claims `agents.md` (see CURSOR_AI_INSTRUCTIONS); the
            // classifier attributes a match to the platform whose watcher POSTed the
            // file, so the shared basename here is intentional and not a conflict.
            pattern: 'agents.md',
            matchType: 'basename',
            category: 'rule',
            locality: 'workspace',
            requireMarkdownExt: true,
        },
        {
            pattern: 'agents.override.md',
            matchType: 'basename',
            category: 'rule',
            locality: 'workspace',
            requireMarkdownExt: true,
        },
        // User ~/.codex/skills/ and project <root>/.codex/skills/. Global locality
        // matches /.copilot/skills/ and does not turn ~/.codex into a project.
        // A renamed $CODEX_HOME (directory not named .codex) does not match;
        // checkSkills does not cover that tree (KIR-4747).
        {
            pattern: '/.codex/skills/',
            matchType: 'directory',
            category: 'skill',
            locality: 'global',
            basenameFilter: 'SKILL.md',
        },
    ],
};
export const PLATFORM_REGISTRY = {
    cursor: {
        displayName: 'Cursor',
        // Per-session JSONL agent transcript, nested in a directory named after
        // the same id. Cursor also mirrors these chats into the shared SQLite KV
        // store (globalStorage/state.vscdb, keyed by composer id), which is what
        // the earlier "SQLite only, no session id" note here described — but the
        // on-disk transcript is the useful artifact for an analyst, and the hook
        // payload does carry the id (KIR-3765).
        //
        // Verified against a live hook capture: `conversation_id` on the Cursor
        // hook payload equals the agent-transcripts directory name, and the
        // transcript inside repeats it as the filename.
        sessionTranscriptLocation: '~/.cursor/projects/<workspace>/agent-transcripts/{sessionId}/{sessionId}.jsonl',
        released: true,
        hasExtensions: true,
        extensionEditor: 'cursor',
        trustedExtensionPlatform: 'cursor',
        // `cursor` is the id the Go agent's Cursor detector emits. The Users tab
        // surfaces it only while `platform_cursor_agent` is on for the org
        // (`getSupportedShadowAiToolIds`). No code ever sent `Cursor`; it stays
        // until a check of stored shadow_ai_tools / shadow_ai_running_processes
        // rows confirms none use it.
        shadowAiToolIds: ['cursor', 'Cursor'],
        // Cursor is a VS Code fork and bundles the stock vscode.* extensions
        // (git, typescript-language-features, …) alongside its own (KIR-4726).
        builtinExtensionPrefixes: ['cursor.', 'anysphere.', 'vscode.'],
        // VS Code bundles its JavaScript debugger and profiler (KIR-4726); its
        // forks ship the same set. Exact ids: ms-vscode.* also publishes
        // marketplace extensions (e.g. ms-vscode.js-debug-nightly).
        builtinExtensionIds: [
            'ms-vscode.js-debug',
            'ms-vscode.js-debug-companion',
            'ms-vscode.vscode-js-profile-table',
            // Cursor's bundled themes have no publisher: vscode-core reports
            // `undefined_publisher.cursor-themes`, the agent the bare name.
            'undefined_publisher.cursor-themes',
            'cursor-themes',
        ],
        officialPublishers: ['cursor', 'anysphere', 'cursor-ai-official'],
        aiInstructions: CURSOR_AI_INSTRUCTIONS,
    },
    claude: {
        displayName: 'Claude Code',
        // Per-session JSONL transcript under the project's slug directory.
        sessionTranscriptLocation: '~/.claude/projects/<workspace>/{sessionId}.jsonl',
        released: true,
        hasExtensions: false,
        extensionEditor: undefined,
        trustedExtensionPlatform: undefined,
        shadowAiToolIds: [
            'claude.ai',
            'claude-code',
            'claude-code-ext',
            'anthropic.claude-code',
            'Claude Code',
        ],
        builtinExtensionPrefixes: [],
        officialPublishers: [],
        aiInstructions: CLAUDE_AI_INSTRUCTIONS,
    },
    claude_desktop: {
        displayName: 'Claude Desktop',
        // macOS session bundles (see claudeDesktopAppDir). The session lives one
        // level deeper (<sub-uuid>/local_<uuid>.json) which we can't reconstruct,
        // so we point at the per-session directory.
        //
        // Claude Desktop keeps TWO session roots side by side (KIR-3978): Code-tab
        // sessions under `claude-code-sessions/`, and Cowork/Chat sessions under
        // `local-agent-mode-sessions/`. A session id resolves to exactly one of
        // them and we cannot tell which from the id alone, so the alt template
        // names the other rather than sending readers to a path that doesn't
        // exist — the same reason `vscode` carries one.
        sessionTranscriptLocation: '~/Library/Application Support/Claude/claude-code-sessions/{sessionId}/',
        sessionTranscriptLocationAlt: {
            when: 'If the session was a Cowork or Chat conversation',
            template: '~/Library/Application Support/Claude/local-agent-mode-sessions/{sessionId}/',
        },
        released: false,
        // The agent discovers Claude Desktop DXT extensions
        // (extensions/agent/platforms/claude_desktop/extensions.go scans
        // `~/Library/Application Support/Claude/Extensions/`). DXT is its own
        // format — distinct from the VS Code marketplace stream — so
        // extensionEditor / trustedExtensionPlatform get a dedicated value
        // rather than reusing the `vscode` editor used by copilot/cursor.
        hasExtensions: true,
        extensionEditor: 'claude_desktop',
        trustedExtensionPlatform: 'claude_desktop',
        // Identifiers emitted verbatim by the shadow-AI detector for the
        // Claude Desktop app (extensions/agent/pkg/shadowai/tools/claude.go:18).
        // The hyphenated `claude-desktop` is the tool_id stored in
        // shadow_ai_tools / shadow_ai_running_processes; without it here the
        // user_security_posture CASE falls through to ELSE and the row is
        // bucketed as 'unknown' instead of 'supported'.
        shadowAiToolIds: ['claude-desktop', 'Claude Desktop'],
        builtinExtensionPrefixes: [],
        officialPublishers: [],
        aiInstructions: CLAUDE_DESKTOP_AI_INSTRUCTIONS,
    },
    // KIR-3908: provenance marker, NOT an installable endpoint. Scans arriving
    // through the Cowork/Claude Desktop hook plugin (POST /api/hooks/scans) are
    // stamped with this client type so alerts render "Claude Desktop (via hook)"
    // and are distinguishable from a locally-installed Claude Desktop. It has no
    // agent install, no extensions, and is not in AGENT_PLATFORM_IDS, so it never
    // shows on the Get Started coverage banner.
    claude_desktop_hook: {
        displayName: 'Claude Desktop (via hook)',
        sessionTranscriptLocation: undefined,
        released: false,
        hasExtensions: false,
        extensionEditor: undefined,
        trustedExtensionPlatform: undefined,
        shadowAiToolIds: [],
        builtinExtensionPrefixes: [],
        officialPublishers: [],
    },
    // KIR-4703: provenance marker, NOT an installable endpoint. Browser-agent
    // activity alerts (a monitored/blocked site an agent drove Chrome to, or a
    // Claude Code WebFetch) are stamped with this client type so the Alerts page
    // renders "Browser AI Agent" as the Source. No agent install, no extensions,
    // not in AGENT_PLATFORM_IDS, so it never shows on the Get Started banner.
    browser_agent: {
        displayName: 'Browser AI Agent',
        sessionTranscriptLocation: undefined,
        released: false,
        hasExtensions: false,
        extensionEditor: undefined,
        trustedExtensionPlatform: undefined,
        shadowAiToolIds: [],
        builtinExtensionPrefixes: [],
        officialPublishers: [],
    },
    copilot: {
        displayName: 'GitHub Copilot',
        // VS Code's core per-session chat serialization. The earlier "SQLite only,
        // no session id" note here was wrong on both counts.
        //
        // The id mapping is established from VS Code's hook implementation rather
        // than a live capture: `chatHookService.ts` derives the payload's
        // `transcript_path` from the very id it sends as `session_id` (it calls
        // `getTranscriptPath(sessionId)` after flushing that session), so the
        // session's on-disk artifacts are keyed by the id we stamp on the alert.
        // Read at
        // https://github.com/microsoft/vscode-copilot-chat/blob/4995bb86/src/extension/chat/vscode-node/chatHookService.ts
        // Corroborated on disk: these filenames are exactly those UUIDs.
        //
        // Deliberately NOT the Copilot extension's own
        // `GitHub.copilot-chat/transcripts/{sessionId}.jsonl`, which is what
        // `transcript_path` resolves to and is the more readable artifact (a flat
        // event stream, versus this file's versioned `{kind, v}` record stream).
        // It is written only for *some* sessions: on a sample machine 5 transcripts
        // existed against 25 sessions in this workspaceStorage tree, including none
        // for the most recent one. Every transcript id had a chatSessions
        // counterpart and not the reverse — a strict superset — and a Location line
        // pointing at a file that usually isn't there is worse than a less readable
        // one that is. Revisit if what gates transcript writing turns out to be
        // something we can rely on.
        //
        // Legacy sessions use `.json` rather than `.jsonl`, in BOTH roots below —
        // 11 of the 35 sessions on that machine, split 7 of 25 under
        // workspaceStorage and 4 of 10 under the empty-window root. Both templates
        // therefore name `.jsonl` for recent sessions only; a reader who finds
        // nothing should retry with `.json` whichever path they took.
        //
        // The id-to-filename mapping is verified against a live session, not just
        // the source read above: a VS Code chat reporting session id
        // c54804c7-… wrote exactly `<that id>.jsonl`, containing that
        // conversation's prompt. What that capture also exposed is that the *root*
        // varies: that window had no folder open (its workspaceStorage dir has no
        // `workspace.json`), so VS Code wrote the session under
        // `globalStorage/emptyWindowChatSessions/` instead of the workspace tree
        // below — see `sessionTranscriptLocationAlt`. That root held 10 of the 35
        // sessions on that machine, too many to leave unmentioned. The filename is
        // the invariant across both roots; the directory is not.
        //
        // macOS-only, like `claude_desktop`: Windows is
        // `%APPDATA%\Code\User\workspaceStorage\...` and Linux
        // `~/.config/Code/User/workspaceStorage/...`. `<workspaceHash>` is an
        // opaque hash of the workspace URI — unlike Cursor's readable slug an
        // analyst can't resolve it by inspection, so this narrows the search to
        // one directory rather than naming a file outright (each hash dir has a
        // `workspace.json` mapping it back to a project folder).
        //
        // The hook payload also carries `transcript_path` outright, which would
        // give a fully resolved path with no placeholder and no per-OS branching —
        // but it inherits the same coverage gap (it points at the transcripts file
        // above), it is a new field end to end, and it would put developer home
        // directories into alert payloads. Recorded as an option, not queued work.
        sessionTranscriptLocation: '~/Library/Application Support/Code/User/workspaceStorage/<workspaceHash>/chatSessions/{sessionId}.jsonl',
        sessionTranscriptLocationAlt: {
            when: 'If the window had no folder open',
            template: '~/Library/Application Support/Code/User/globalStorage/emptyWindowChatSessions/{sessionId}.jsonl',
        },
        released: true,
        hasExtensions: true,
        extensionEditor: 'vscode',
        trustedExtensionPlatform: 'vscode',
        shadowAiToolIds: [
            'github-copilot',
            'github.copilot',
            'github-copilot-chat',
            'github.copilot-chat',
            // NOTE: 'github-copilot-cli' intentionally lives on the github_copilot_cli
            // entry (the terminal CLI platform), NOT here. KIR-3317 parked it on this
            // IDE entry before the CLI platform existed; KIR-3179 moved it so CLI
            // signals correlate to the CLI platform. The shadowAiToolId→platform map
            // forbids the same id on two entries.
            'GitHub Copilot',
            'GitHub Copilot Chat',
        ],
        builtinExtensionPrefixes: ['github.copilot', 'vscode.'],
        // VS Code bundles its JavaScript debugger and profiler (KIR-4726); its
        // forks ship the same set. Exact ids: ms-vscode.* also publishes
        // marketplace extensions (e.g. ms-vscode.js-debug-nightly).
        builtinExtensionIds: [
            'ms-vscode.js-debug',
            'ms-vscode.js-debug-companion',
            'ms-vscode.vscode-js-profile-table',
        ],
        // GitHub is already covered by the major-tech-company manual row in
        // lists/trusted-sources.ts and isn't Copilot-specific.
        officialPublishers: [],
        aiInstructions: COPILOT_AI_INSTRUCTIONS,
    },
    github_copilot_cli: {
        // The terminal `copilot` CLI agent (KIR-3179) — distinct from the
        // `copilot` IDE-extension entry above (KIR-2977 / KIR-3215). The CLI has
        // no VS Code-style extension marketplace; its "plugins" are custom agents
        // discovered by the Go agent, and MCP servers come from
        // `~/.copilot/mcp-config.json` / `.mcp.json`. Offline default — runtime
        // release state is governed by the `platform_github_copilot_cli`
        // Flagsmith flag (mirrors the Windsurf / Antigravity rollout pattern).
        displayName: 'GitHub Copilot',
        // The Copilot CLI writes per-session event logs under
        // ~/.copilot/session-state/<sessionId>/events.jsonl (honors COPILOT_HOME);
        // the agentStop hook hands the agent the concrete transcript path to scan.
        sessionTranscriptLocation: '~/.copilot/session-state/{sessionId}/events.jsonl',
        released: false,
        hasExtensions: false,
        extensionEditor: undefined,
        trustedExtensionPlatform: undefined,
        // Distinct from the IDE copilot ids so Shadow AI correlation maps CLI
        // signals to this platform (the tool_id → platform map is last-wins, so
        // these must not collide with the `copilot` IDE entry's ids).
        shadowAiToolIds: [
            'github-copilot-cli',
            'github.copilot-cli',
            'copilot-cli',
            'GitHub Copilot CLI',
        ],
        builtinExtensionPrefixes: [],
        officialPublishers: [],
        aiInstructions: GITHUB_COPILOT_CLI_AI_INSTRUCTIONS,
    },
    vscode: {
        displayName: 'VS Code',
        // Same application as `copilot` above (both declare extensionEditor /
        // trustedExtensionPlatform 'vscode' and read the same workspaceStorage
        // tree), so this is one decision with that entry — see its comment for the
        // evidence and the per-OS caveats. Keep the two in lockstep.
        //
        // The agent plugin `platforms/vscode` emits `Kirin-Client-Type: vscode`
        // (KIR-4732), so new extension-security findings from that scanner
        // render as VS Code. vscode-core still reports client-type `copilot`
        // until KIR-3354 fully removes the Kirin VS Code extension — two
        // labels for the same editor is expected during that window. Keep
        // both registry keys; do not delete `copilot`.
        sessionTranscriptLocation: '~/Library/Application Support/Code/User/workspaceStorage/<workspaceHash>/chatSessions/{sessionId}.jsonl',
        sessionTranscriptLocationAlt: {
            when: 'If the window had no folder open',
            template: '~/Library/Application Support/Code/User/globalStorage/emptyWindowChatSessions/{sessionId}.jsonl',
        },
        released: true,
        hasExtensions: true,
        extensionEditor: 'vscode',
        trustedExtensionPlatform: 'vscode',
        shadowAiToolIds: ['vscode', 'vs-code', 'VS Code'],
        builtinExtensionPrefixes: ['vscode.', 'github.copilot'],
        // VS Code bundles its JavaScript debugger and profiler (KIR-4726); its
        // forks ship the same set. Exact ids: ms-vscode.* also publishes
        // marketplace extensions (e.g. ms-vscode.js-debug-nightly).
        builtinExtensionIds: [
            'ms-vscode.js-debug',
            'ms-vscode.js-debug-companion',
            'ms-vscode.vscode-js-profile-table',
        ],
        // microsoft / vscode / github are major-tech-company entries handled
        // by the manual rows in lists/trusted-sources.ts; nothing VS Code-
        // specific belongs here.
        officialPublishers: [],
    },
    antigravity: {
        displayName: 'Antigravity',
        // No officially documented per-session transcript path yet; leave
        // undefined until one is verified rather than guessing.
        sessionTranscriptLocation: undefined,
        /**
         * KIR-2217: Antigravity is recognized as a Shadow AI tool (see
         * `google-antigravity` in `detection-catalog.ts`). Offline default —
         * runtime release state is governed by the `platform_antigravity`
         * Flagsmith feature flag (mirrors the Windsurf rollout pattern). No
         * branded SVG ships yet; `getPlatformIcon` returns the generic
         * lucide `Code2` fallback for this id until design provides the real
         * mark (see `PLATFORM_ICONS` in
         * `apps/frontend/src/components/shared/platform/platform-utils.ts`).
         */
        released: false,
        hasExtensions: false,
        extensionEditor: undefined,
        trustedExtensionPlatform: undefined,
        shadowAiToolIds: ['google-antigravity', 'antigravity', 'Antigravity'],
        builtinExtensionPrefixes: [],
        officialPublishers: [],
    },
    gemini_cli: {
        displayName: 'Gemini CLI',
        // Per-project chat sessions live under a hashed project dir. Files are
        // timestamped rather than keyed by the session id, so we point at the
        // directory. See
        // https://github.com/google-gemini/gemini-cli/blob/main/docs/cli/session-management.md
        sessionTranscriptLocation: '~/.gemini/tmp/<projectHash>/chats/',
        /**
         * KIR-3178: Google's Gemini CLI — a hook-driven terminal agent (like
         * Claude Code, not a VS Code fork). Offline default — runtime release
         * state is governed by the `platform_gemini_cli` Flagsmith feature flag
         * (mirrors the Windsurf / Antigravity rollout pattern). Branded icon
         * ships at `apps/frontend/src/assets/images/gemini_cli.png` via the
         * `GeminiCliIcon` component, wired into `PLATFORM_ICONS` and the plugin
         * source badge. Gemini CLI
         * has no VS Code-style marketplace extensions, so `hasExtensions` is
         * false. shadowAiToolIds uses `gemini-cli` (NOT bare `gemini`) to avoid
         * colliding with the gemini.google.com web-chatbot tool ids.
         */
        released: false,
        hasExtensions: false,
        extensionEditor: undefined,
        trustedExtensionPlatform: undefined,
        shadowAiToolIds: ['gemini-cli', 'Gemini CLI'],
        builtinExtensionPrefixes: [],
        officialPublishers: [],
        aiInstructions: GEMINI_AI_INSTRUCTIONS,
    },
    codex: {
        displayName: 'Codex',
        // Per-session JSONL "rollout" transcript. Files are nested under UTC
        // date dirs and the filename ends with the session UUID, so we match
        // with a glob rather than a single deterministic path. See
        // https://github.com/openai/codex (CODEX_HOME defaults to ~/.codex).
        sessionTranscriptLocation: '~/.codex/sessions/**/rollout-*-{sessionId}.jsonl',
        /**
         * KIR-3334: OpenAI Codex — a hook-driven terminal/UI agent (like Claude
         * Code / Gemini CLI, not a VS Code fork), supported entirely through the
         * Kirin go-agent with no IDE extension. Offline default — runtime release
         * state is governed by the `platform_codex` Flagsmith feature flag
         * (mirrors the Gemini CLI / Windsurf rollout pattern). Codex has no VS
         * Code-style marketplace extensions, so `hasExtensions` is false.
         * shadowAiToolIds lists the tool_ids emitted by the shadow-AI detector
         * (extensions/agent/pkg/shadowai/tools/codex.go) so user_security_posture
         * buckets Codex as 'supported' rather than 'unknown'. KIR-3334 split Codex
         * into `codex-cli` and `codex-desktop`; the legacy merged `openai-codex`
         * id is kept for older agents still reporting it.
         */
        released: false,
        hasExtensions: false,
        extensionEditor: undefined,
        trustedExtensionPlatform: undefined,
        shadowAiToolIds: ['codex-cli', 'codex-desktop', 'openai-codex', 'Codex'],
        builtinExtensionPrefixes: [],
        officialPublishers: [],
        aiInstructions: CODEX_AI_INSTRUCTIONS,
    },
    windsurf: {
        // Windsurf was rebranded to Devin Desktop (KIR-3873). The platform id
        // stays `windsurf` — it is the join key for shadow-AI tool ids, flags
        // (`platform_windsurf`) and extension-editor lookups. Only the label moves.
        displayName: 'Devin Desktop',
        // Per-session Cascade "trajectory" files (encrypted *.pb) live under this
        // dir; the filenames aren't the reported session id and the contents are
        // AES-encrypted at rest, so we point at the directory rather than a file.
        sessionTranscriptLocation: '~/.codeium/windsurf/cascade/',
        /** Offline default — runtime release state is governed by the
         *  `platform_windsurf` Flagsmith feature flag. */
        released: false,
        hasExtensions: true,
        extensionEditor: 'windsurf',
        trustedExtensionPlatform: 'windsurf',
        // Not extended with 'Devin Desktop' by KIR-3873, deliberately. These are
        // tool_ID values, and the agent's id is unchanged by the rename
        // (tool_id and tool_name are separate columns), so no Devin-shaped id can
        // ever be written. Adding a name-shaped key here would only widen a SQL
        // CASE and the posture query's IN-lists for rows that cannot exist.
        //
        // The legacy name-shaped 'Windsurf' entry is left untouched. What wrote
        // it is NOT established — the staging `SELECT DISTINCT tool_id, tool_name`
        // that would have settled it was not run, so this took the safe default of
        // changing nothing. Removing it is a separate, evidence-led change.
        shadowAiToolIds: ['windsurf', 'Windsurf'],
        /**
         * Windsurf ships with two built-in publishers: `vscode.*` (inherited
         * from the upstream VS Code fork — vscode.git, vscode.python-language-features,
         * etc.) and `codeium.*` (Windsurf-specific bundles like
         * codeium.windsurf, codeium.windsurf-dev-containers,
         * codeium.windsurf-remote-openssh, codeium.windsurf-remote-wsl).
         */
        builtinExtensionPrefixes: [
            'vscode.',
            'codeium.',
            // e.g. windsurf.theme-windsurf ships under publisher "windsurf".
            'windsurf.',
        ],
        // VS Code bundles its JavaScript debugger and profiler (KIR-4726); its
        // forks ship the same set. Exact ids: ms-vscode.* also publishes
        // marketplace extensions (e.g. ms-vscode.js-debug-nightly).
        builtinExtensionIds: [
            'ms-vscode.js-debug',
            'ms-vscode.js-debug-companion',
            'ms-vscode.vscode-js-profile-table',
        ],
        officialPublishers: ['codeium'],
        yolo: WINDSURF_YOLO,
        aiInstructions: WINDSURF_AI_INSTRUCTIONS,
    },
    // KIR-4646 / KIR-5046: attribution label, NOT an installable endpoint or a
    // scanning platform. The backend assigns it to instruction-file scans of
    // markdown files (`.md` / `.mdc`) and of files under `.agents/skills` (see
    // resolveAttributedPlatform in apps/backend) so they are not labeled with
    // whichever IDE happened to discover them. Not in AGENT_PLATFORM_IDS, so it
    // never shows on the Get Started coverage banner.
    multi_tool: {
        displayName: 'Multi-IDE / Platform',
        sessionTranscriptLocation: undefined,
        released: false,
        hasExtensions: false,
        extensionEditor: undefined,
        trustedExtensionPlatform: undefined,
        shadowAiToolIds: [],
        builtinExtensionPrefixes: [],
        officialPublishers: [],
        aiInstructions: MULTI_TOOL_AI_INSTRUCTIONS,
    },
};
export const PLATFORM_IDS = Object.keys(PLATFORM_REGISTRY);
// ---------------------------------------------------------------------------
// Runtime helpers (used by contracts and domain logic)
// ---------------------------------------------------------------------------
/**
 * Narrow an arbitrary string (e.g. a `Kirin-Client-Type` HTTP header value or
 * a database-stored `ide_platform` column) into a typed `PlatformId`, or
 * `undefined` when the value does not match any registry key.
 *
 * Use this at every boundary that accepts untrusted input so callers (the
 * AI instructions classifier, scan handlers, etc.) can pass the result
 * through type-safe APIs without casts. Unknown values gracefully degrade to
 * `undefined` rather than silently biasing precedence toward a non-existent
 * platform.
 */
export function toPlatformId(value) {
    if (!value) {
        return undefined;
    }
    return PLATFORM_IDS.includes(value)
        ? value
        : undefined;
}
/**
 * Platform id assigned to files several tools read: every markdown
 * instruction file (KIR-5046) and anything under a shared agent-config tree.
 */
export const MULTI_TOOL_PLATFORM_ID = 'multi_tool';
/**
 * Whether a file sits under a shared agent-config tree — today only
 * `.agents/skills/` — at any depth: `~/.agents/skills/…` and project copies
 * such as `~/Documents/proj/.agents/skills/…`.
 *
 * Matches the adjacent segments `.agents` then `skills`, case-insensitively
 * on every OS and with either separator. `.devin/skills`, `.windsurf/skills`,
 * `.claude/skills` and look-alikes (`foo.agents/skills`, `.agents/skillsx`)
 * do not match. This is the only implementation of the rule; the agent
 * renders the backend's result instead of matching paths itself.
 */
export function isSharedAgentConfigPath(filePath) {
    if (!filePath) {
        return false;
    }
    const segments = filePath.replace(/\\/g, '/').toLowerCase().split('/');
    for (let i = 0; i < segments.length - 2; i++) {
        if (segments[i] === '.agents' && segments[i + 1] === 'skills') {
            return true;
        }
    }
    return false;
}
export function getExtensionPlatformIds() {
    return PLATFORM_IDS.filter((id) => PLATFORM_REGISTRY[id].hasExtensions);
}
export function getExtensionSourceDisplayNames() {
    return PLATFORM_IDS.filter(id => PLATFORM_REGISTRY[id].hasExtensions).map(id => ({ id, displayName: PLATFORM_REGISTRY[id].displayName }));
}
export function getUniqueExtensionEditors() {
    return [
        ...new Set(PLATFORM_IDS.map(id => PLATFORM_REGISTRY[id].extensionEditor).filter((e) => e !== undefined)),
    ];
}
export function getUniqueTrustedPlatforms() {
    return [
        ...new Set(PLATFORM_IDS.map(id => PLATFORM_REGISTRY[id].trustedExtensionPlatform).filter((p) => p !== undefined)),
    ];
}
/**
 * Build a map from Shadow AI `tool_id` to Kirin `PlatformId`.
 * Used by `user-security-posture.repository` to normalize Shadow AI signals.
 */
export function buildShadowAiToolIdToClientMap() {
    const map = {};
    for (const id of PLATFORM_IDS) {
        for (const toolId of PLATFORM_REGISTRY[id].shadowAiToolIds) {
            map[toolId] = id;
        }
    }
    return map;
}
/**
 * Build a map from `extensionEditor` to `trustedExtensionPlatform`.
 * Used by extension evaluation to look up the trusted list for an editor.
 */
export function buildEditorToTrustedPlatformMap() {
    const map = {};
    for (const meta of Object.values(PLATFORM_REGISTRY)) {
        if (meta.extensionEditor !== undefined &&
            meta.trustedExtensionPlatform !== undefined) {
            map[meta.extensionEditor] = meta.trustedExtensionPlatform;
        }
    }
    return map;
}
/**
 * Build a map from `ExtensionPlatformId` to its set of lowercased built-in
 * publisher prefixes. Used by the backend `isBuiltinExtension` helper to
 * classify extensions in the inventory.
 *
 * Returns a fully-populated record covering every `ExtensionPlatformId` —
 * platforms with no built-in publishers contribute an empty set rather than
 * being omitted, so callers can index without undefined checks.
 */
export function buildBuiltinPrefixesByExtensionSource() {
    const map = {};
    for (const id of getExtensionPlatformIds()) {
        map[id] = new Set(PLATFORM_REGISTRY[id].builtinExtensionPrefixes.map(p => p.toLowerCase()));
    }
    return map;
}
/** Pre-computed: Shadow AI tool_id → Kirin PlatformId */
export const SHADOW_AI_TOOL_ID_TO_CLIENT_MAP = buildShadowAiToolIdToClientMap();
/** Pre-computed: extensionEditor → trustedExtensionPlatform */
export const EDITOR_TO_TRUSTED_PLATFORM_MAP = buildEditorToTrustedPlatformMap();
/** Pre-computed: ExtensionPlatformId → lowercased built-in publisher prefixes. */
export const BUILTIN_PREFIXES_BY_EXTENSION_SOURCE = buildBuiltinPrefixesByExtensionSource();
/**
 * Map from `ExtensionPlatformId` to its set of lowercased exact built-in
 * extension ids (`builtinExtensionIds`); empty sets for platforms with none.
 */
export function buildBuiltinIdsByExtensionSource() {
    const map = {};
    for (const id of getExtensionPlatformIds()) {
        const entry = PLATFORM_REGISTRY[id];
        map[id] = new Set((entry.builtinExtensionIds ?? []).map(e => e.toLowerCase()));
    }
    return map;
}
/** Pre-computed: ExtensionPlatformId → lowercased exact built-in ids. */
export const BUILTIN_IDS_BY_EXTENSION_SOURCE = buildBuiltinIdsByExtensionSource();
/**
 * Flatten `PLATFORM_REGISTRY[*].officialPublishers` into wildcard rows that
 * the backend can splice into `TRUSTED_SOURCES_DATA`. Each publisher becomes
 * one row of shape `[publisher, '*', '*', reason]`.
 *
 * !!! TRUST IS GLOBAL, NOT PLATFORM-SCOPED !!!
 * The returned rows are consumed by `parseTrustedSources` which produces
 * publisher-only entries — once a publisher appears here, it is trusted in
 * every extension inventory regardless of the platform that owns it.
 * See the TSDoc on `PlatformMetadata.officialPublishers` for the full
 * rationale and the security checks that get skipped as a result.
 */
export function getRegistryTrustedPublisherRows() {
    const rows = [];
    for (const id of PLATFORM_IDS) {
        const meta = PLATFORM_REGISTRY[id];
        for (const publisher of meta.officialPublishers) {
            rows.push([
                publisher,
                '*',
                '*',
                `Official ${meta.displayName} publisher (platform registry)`,
            ]);
        }
    }
    return rows;
}
//# sourceMappingURL=platform-registry.js.map